import { useEffect, useState } from "react";
import { Beer, Mail } from "lucide-react";
import { api } from "@/lib/api";

/**
 * /business/pubs — pub-facing pitch page.
 *
 * Renders the interactive Canva deck in a responsive 16:9 container.
 * iter177 — the iframe `src` now comes from the admin-managed
 * `marketing_content.pitch_deck` document, fetched via the public
 * `GET /api/marketing/pitch-deck` endpoint. Super-Admins swap the
 * URL from the /admin dashboard (`PitchDeckManagerCard`) — no code
 * change or redeploy required.
 */
const PLATFORM_DEFAULT_URL =
  "https://www.canva.com/design/DAHQCvIqVvc/QOdsfVcvMF3nruWNpkiMRw/view?embed";
const PLATFORM_DEFAULT_HREF =
  "https://www.canva.com/design/DAHQCvIqVvc/QOdsfVcvMF3nruWNpkiMRw/view?utm_content=DAHQCvIqVvc&utm_campaign=designshare&utm_medium=embeds&utm_source=link";

/** Derive a human-friendly "open in Canva" link from the raw embed URL.
 *  For a URL like `https://www.canva.com/design/<id>/<hash>/view?embed`
 *  we drop the `?embed` query so the link opens the interactive deck
 *  outside the iframe. */
function toShareHref(embedUrl) {
  if (!embedUrl) return PLATFORM_DEFAULT_HREF;
  try {
    const u = new URL(embedUrl);
    u.searchParams.delete("embed");
    return u.toString();
  } catch {
    return embedUrl;
  }
}

export default function BusinessPubsPage() {
  const [embedUrl, setEmbedUrl] = useState(PLATFORM_DEFAULT_URL);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/marketing/pitch-deck");
        if (alive && data?.canva_embed_url) {
          setEmbedUrl(data.canva_embed_url);
        }
      } catch {
        // Silent fallback to the platform default — the page still works
        // even if the marketing endpoint is transiently unavailable.
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div
      className="min-h-screen bg-white text-zinc-950"
      data-testid="business-pubs-page"
    >
      {/* Top bar --------------------------------------------------------- */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm inline-flex items-center justify-center bg-zinc-950">
            <Beer className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
              Dugsy&apos;s · For pubs &amp; breweries
            </p>
            <h1 className="font-heading text-lg font-bold tracking-tight">The pitch.</h1>
          </div>
        </div>
      </header>

      {/* Canva interactive deck embed ------------------------------------ */}
      <section
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-24"
        data-testid="pitch-canva-section"
      >
        <div className="mb-4 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
            Interactive deck
          </p>
          <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Football Challenge · Pub Pitch Deck
          </h2>
          <p className="mt-1.5 text-sm text-zinc-500">
            Fullscreen the deck below and walk your prospect through it live.
          </p>
        </div>

        {/* 16:9 responsive container. padding-top:56.25% locks the aspect */}
        <div
          className="relative w-full mx-auto rounded-lg overflow-hidden bg-zinc-950"
          style={{
            paddingTop: "56.25%",
            boxShadow: "0 2px 8px 0 rgba(63,69,81,0.16)",
            willChange: "transform",
          }}
          data-testid="pitch-canva-frame"
        >
          <iframe
            key={embedUrl}
            loading="lazy"
            title="Football Challenge Pub Pitch Deck"
            src={embedUrl}
            allowFullScreen
            allow="fullscreen"
            className="absolute inset-0 w-full h-full border-0"
            data-testid="pitch-canva-iframe"
          />
        </div>

        <p className="mt-3 text-center text-[11px] text-zinc-500">
          <a
            href={toShareHref(embedUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-zinc-900"
            data-testid="pitch-canva-link"
          >
            Football Challenge Pub Pitch Deck
          </a>{" "}
          by Dugsy
        </p>

        {/* Contact CTA ----------------------------------------------------- */}
        <div
          className="mt-10 border-t border-zinc-200 pt-8 text-center"
          data-testid="pitch-contact-block"
        >
          <p className="text-sm text-zinc-700 max-w-2xl mx-auto leading-relaxed">
            Contact us for further information and the next step to driving your mid-week footfall:
          </p>
          <a
            href="mailto:carl@amblemead.biz"
            data-testid="pitch-contact-email"
            className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-heading font-bold text-sm uppercase tracking-[0.14em] transition-colors"
          >
            <Mail className="h-4 w-4" />
            carl@amblemead.biz
          </a>
        </div>
      </section>
    </div>
  );
}
